import { useLogConversionEvent } from '@bfa/capi';
import {
  ConversionEventName,
  ConversionPageCategory,
} from '@bfa/capi/dist/constants';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { v4 as uuid } from 'uuid';

import { SUBSCRIPTION_FLOWS } from '../../constants';
import { GQLUserSubscription } from '../../gql.generated';
import { getVariableName, GTMVariables, GTMVariablesPrefix } from './constants';
import GoogleTagManagerVariablesHeadScript from './googleTagManagerVariablesHeadScript';

export type GtmEventSubscriptionType = {
  id: string;
  cost:
    | {
        amount: string;
        currency: string;
      }
    | undefined;
};

type Subscribed2Type = {
  GLAMBAG?: GtmEventSubscriptionType;
  FULLSIZE?: GtmEventSubscriptionType;
  GBX?: GtmEventSubscriptionType;
};

type GTM_EVENTS = {
  SUBSCRIBED: GtmEventSubscriptionType;
  SUBSCRIBED2: Subscribed2Type;
};

export type GtmEventsScriptProps = {
  flow?: string;
  gbSubscription?: GQLUserSubscription;
  gbpSubscription?: GQLUserSubscription;
  gbxSubscription?: GQLUserSubscription;
  userEmail: string;
  userId: string;
  userSid: string;
  paymentType?: string;
  subscriptionCadence?: string;
  subscriptionProgram?: string;
};

type GiftingConfirmGtmEventsScriptProps = {
  userEmail: string;
  giftCardValue: number;
};

type DeferredEventType = {
  name: string;
};

const DEFERRED_EVENT_NAMES_BY_FLOW = {
  ACTIVATION: ['SUB_FORM'],
  REACTIVATE: ['REACTIVATE', 'REACTIVATE_PROMO'],
};

type DeferredEventsByFlowKey = keyof typeof DEFERRED_EVENT_NAMES_BY_FLOW;

const isValidDeferredEvent = (
  flow: SUBSCRIPTION_FLOWS,
  deferredEvents: string | string[] | undefined
): boolean => {
  if (!deferredEvents) return false;

  const eventsReceived = JSON.parse(
    deferredEvents as string
  ) as DeferredEventType[];

  const validEventNamesForFlow =
    DEFERRED_EVENT_NAMES_BY_FLOW[flow.toString() as DeferredEventsByFlowKey];

  if (!validEventNamesForFlow) return false;

  return eventsReceived.some(
    (event) =>
      event && event.name && validEventNamesForFlow.includes(event.name)
  );
};

const shouldAddGtmEventsOnHeader = (
  flow: string | undefined,
  deferredEvents: string | string[] | undefined
): boolean => {
  if (!flow) return false;

  const subscriptionFlow = flow.toUpperCase() as SUBSCRIPTION_FLOWS;

  if (!subscriptionFlow) return false;

  switch (subscriptionFlow) {
    case SUBSCRIPTION_FLOWS.ADD:
    case SUBSCRIPTION_FLOWS.SWITCH:
    case SUBSCRIPTION_FLOWS.ADDONS:
    case SUBSCRIPTION_FLOWS.UP:
    case SUBSCRIPTION_FLOWS.REACTIVATE:
    case SUBSCRIPTION_FLOWS.ACTIVATION:
      return true;

    default:
      return isValidDeferredEvent(subscriptionFlow, deferredEvents);
  }
};

export const getSubscriptionForGTMEvent = (
  sub: GQLUserSubscription
): GtmEventSubscriptionType => {
  const item =
    sub.subscriptionItems.length > 0 ? sub.subscriptionItems[0] : undefined;
  return {
    id: sub.id!,
    cost: {
      amount: item?.price?.amount?.toString() ?? '',
      currency: item?.price?.currency ?? '',
    },
  };
};

const getUserStatus = (flow?: string): string => {
  const subscriptionFlow = flow?.toUpperCase() as SUBSCRIPTION_FLOWS;

  return subscriptionFlow === SUBSCRIPTION_FLOWS.ACTIVATION
    ? 'New'
    : 'Returning';
};

export const getGtmEvents = (
  gbSubscription: GQLUserSubscription | undefined,
  gbpSubscription: GQLUserSubscription | undefined,
  gbxSubscription: GQLUserSubscription | undefined
): GTM_EVENTS => {
  let subscribed = {};
  let subscribed2 = {};

  if (gbSubscription) {
    const gb = getSubscriptionForGTMEvent(gbSubscription);

    subscribed = gb;
    subscribed2 = {
      GLAMBAG: gb,
    };
  }

  if (gbpSubscription) {
    const gbp = getSubscriptionForGTMEvent(gbpSubscription);

    subscribed = gbp;

    subscribed2 = {
      ...subscribed2,
      FULLSIZE: gbp,
    };
  }

  if (gbxSubscription) {
    const gbx = getSubscriptionForGTMEvent(gbxSubscription);

    subscribed = gbx;
    subscribed2 = {
      ...subscribed2,
      GBX: gbx,
    };
  }

  return {
    SUBSCRIBED: subscribed as GtmEventSubscriptionType,
    SUBSCRIBED2: subscribed2,
  };
};

const getScriptContent = (
  data: GTM_EVENTS,
  userEmail: string,
  userId: string,
  userStatus: string,
  userSid: string,
  addGtmEventsData: boolean,
  paymentType?: string,
  subscriptionCadence?: string,
  subscriptionProgram?: string
): string => {
  return `
  ${GTMVariablesPrefix} = {}
  ${getVariableName(GTMVariables.USER_EMAIL)} = '${userEmail}'
  ${
    addGtmEventsData
      ? `${getVariableName(GTMVariables.GTM_EVENTS)} = ${JSON.stringify(data)}
      ${getVariableName(GTMVariables.SUBSCRIPTION_ID)} = '${data.SUBSCRIBED.id}'
      ${getVariableName(GTMVariables.SUBSCRIPTION_COST)} = '${
          data.SUBSCRIBED.cost?.amount
        }'
      ${getVariableName(
        GTMVariables.SUBSCRIPTION_PAYMENT_TYPE
      )} = '${paymentType}'
      ${getVariableName(
        GTMVariables.SUBSCRIPTION_CADENCE
      )} = '${subscriptionCadence}'
      ${getVariableName(
        GTMVariables.SUBSCRIPTION_PROGRAM
      )} = '${subscriptionProgram}'
      ${getVariableName(GTMVariables.USER_STATUS)} = '${userStatus}'
      ${getVariableName(GTMVariables.USER_ID)} = '${userId}'
      ${getVariableName(GTMVariables.USER_SID)} = '${userSid}'
      ${getVariableName(GTMVariables.IS_COMING_FROM_SPA)} = true
      ${GTMVariables.ENHANCED_CONVERSION_DATA} = {
        ${GTMVariables.ENHANCED_CONVERSION_DATA_EMAIL} : '${userEmail}'
      }
      `
      : ''
  }`;
};

const GtmEventsScript = ({
  flow,
  gbSubscription,
  gbpSubscription,
  gbxSubscription,
  userEmail,
  userId,
  userSid,
  paymentType,
  subscriptionCadence,
  subscriptionProgram,
}: GtmEventsScriptProps) => {
  const router = useRouter();
  const { deferredEvents } = router.query;

  const addGtmEventsData = shouldAddGtmEventsOnHeader(flow, deferredEvents);

  const data = getGtmEvents(gbSubscription, gbpSubscription, gbxSubscription);
  const userStatus = getUserStatus(flow!);
  const conversionId = uuid();

  useEffect(() => {
    if (!addGtmEventsData) return;
    (window as any).dataLayer.push({
      event: 'async-subscribe-purchase-event',
      [GTMVariables.USER_EMAIL]: userEmail,
      [GTMVariables.SUBSCRIPTION_CURRENCY]: data.SUBSCRIBED.cost?.currency,
      [GTMVariables.GTM_EVENTS]: data,
      [GTMVariables.SUBSCRIPTION_ID]: data.SUBSCRIBED.id,
      [GTMVariables.SUBSCRIPTION_COST]: data.SUBSCRIBED.cost?.amount,
      [GTMVariables.SUBSCRIPTION_PAYMENT_TYPE]: paymentType,
      [GTMVariables.SUBSCRIPTION_CADENCE]: subscriptionCadence,
      [GTMVariables.SUBSCRIPTION_PROGRAM]: subscriptionProgram,
      [GTMVariables.USER_STATUS]: userStatus,
      [GTMVariables.USER_ID]: userId,
      [GTMVariables.USER_SID]: userSid,
      [GTMVariables.ENHANCED_CONVERSION_DATA]: {
        [GTMVariables.ENHANCED_CONVERSION_DATA_EMAIL]: userEmail,
      },
      [GTMVariables.CONVERSION_ID]: conversionId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLogConversionEvent({
    event: ConversionEventName.SUBSCRIBE,
    pageCategory: ConversionPageCategory.SUBSCRIBE,
    conversionId,
    shouldFire: addGtmEventsData && !!userId,
    orderId: data.SUBSCRIBED.id,
    currency: data.SUBSCRIBED.cost?.currency ?? '',
    value: data.SUBSCRIBED.cost?.amount ?? '',
    subscriptionStatus: userStatus === 'New' ? 'new' : 'recurring',
    subscriptionProgram:
      subscriptionProgram?.toLowerCase() === 'fullsize'
        ? 'boxycharm'
        : (subscriptionProgram?.toLowerCase() as 'glambag' | 'combined'),
  });

  if (!addGtmEventsData) return null;

  const scriptContent = getScriptContent(
    data,
    userEmail,
    userId,
    userStatus,
    userSid,
    addGtmEventsData,
    paymentType,
    subscriptionCadence,
    subscriptionProgram
  );

  return <GoogleTagManagerVariablesHeadScript content={scriptContent} />;
};

export const GiftingConfirmGtmEvents = ({
  userEmail,
  giftCardValue,
}: GiftingConfirmGtmEventsScriptProps) => {
  const scriptContent = `
    ${GTMVariablesPrefix} = {}
    ${getVariableName(GTMVariables.USER_EMAIL)} = '${userEmail}'
  `;

  return <GoogleTagManagerVariablesHeadScript content={scriptContent} />;
};

// eslint-disable-next-line import/no-self-import
export const GtmEvents = dynamic(() => import('.'), {
  ssr: false,
});

export default GtmEventsScript;
